import '../balloon-page.scss';

import { Dispatch, SetStateAction } from 'react';

import { useAppSelector } from '@/app/store/hooks';
import {
  BalloonStatus,
  selectBalloonLoading,
  selectBalloonStatus,
} from '@/entities/balloon';
import { selectUserBalanceByCurrency } from '@/entities/user';
import { type CurrencyCode,getGameCurrencyLimits } from '@/shared/lib/currency';
import { BetPanel } from '@/shared/ui/BetPanel';

import { BalloonHeatButton } from './BalloonHeatButton';
import { BalloonStatRow } from './BalloonStatRow';
import { BalloonTakeButton } from './BalloonTakeButton';

interface BalloonBetPanelProps {
  betAmount: number;
  setBetAmount: Dispatch<SetStateAction<number>>;
  selectedCurrency: CurrencyCode;
  setSelectedCurrency: Dispatch<SetStateAction<CurrencyCode>>;
}

export function BalloonBetPanel({
  betAmount,
  setBetAmount,
  selectedCurrency,
  setSelectedCurrency,
}: BalloonBetPanelProps) {
  const status = useAppSelector(selectBalloonStatus);
  const loading = useAppSelector(selectBalloonLoading);
  const balance = useAppSelector(selectUserBalanceByCurrency(selectedCurrency));
  const { maxBet } = getGameCurrencyLimits('balloon', selectedCurrency);

  const isPreheating = status === BalloonStatus.PREHEATING;
  const isReady = status === BalloonStatus.READY;
  const isHeating = status === BalloonStatus.HEATING;
  const isPaused = status === BalloonStatus.PAUSED;
  const isActive = isPreheating || isReady || isHeating || isPaused;
  const isOver = status === BalloonStatus.WON || status === BalloonStatus.BUSTED;
  const isIdle = status === BalloonStatus.IDLE;

  return (
    <div className="balloon-panel">
      <div className="balloon-panel__action-row">
        <BalloonHeatButton betAmount={betAmount} currency={selectedCurrency} />
        <BalloonTakeButton fallbackBet={betAmount} fallbackCurrency={selectedCurrency} />
      </div>

      <div className="balloon-panel__top">
        {(isIdle || isOver) && (
          <BetPanel
            currency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            onSelectAmount={setBetAmount}
            maxAmount={Math.min(maxBet, balance)}
            disabled={loading || isOver}
          />
        )}
        {isActive && (
          <BalloonStatRow fallbackBet={betAmount} fallbackCurrency={selectedCurrency} />
        )}
      </div>
    </div>
  );
}
